// ─────────────────────────────────────────────────────────────────────────────
// Generic offline layer for every page that has no dedicated offline store
// (patients, prescriptions, doctors/employees, users, roles, branches, brands,
// manufacturers, strengths, taxes, currencies, banks, requisitions, purchase
// requests, purchase returns, partners, notifications, reports, ...).
//
// Reads  : network first; every successful GET is remembered in IndexedDB and
//          served back when there is no connection.
// Writes : tried on the network first; on a network failure the change is
//          applied to the remembered lists right away and queued in the same
//          sync queue the rest of the app uses (offline/sync.js pushes it).
// ─────────────────────────────────────────────────────────────────────────────
import api from '../api/axios';
import {
  apiCacheGet, apiCachePut, apiCacheEntries, enqueue, getQueue, updateQueueItem,
  purgeQueueReferencing, genLocalId, hasPendingQueue,
} from './db';

export const isNetworkError = (error) =>
  !error?.response || error?.code === 'ERR_NETWORK' || error?.message === 'Network Error';
export const isOfflineId = (id) => String(id || '').startsWith('offline-');

const offlineResponse = (data, extra = {}) => ({ data: { success: true, data, ...extra } });

const stable = (params = {}) => {
  const keys = Object.keys(params || {}).filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '').sort();
  return keys.map(k => `${k}=${params[k]}`).join('&');
};
const cacheKey = (url, params) => `${url}?${stable(params)}`;
const ALL = '__all';

const walkText = (val, depth = 0) => {
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (depth > 3) return '';
  if (Array.isArray(val)) return val.map(v => walkText(v, depth + 1)).join(' ');
  if (typeof val === 'object') return Object.values(val).map(v => walkText(v, depth + 1)).join(' ');
  return '';
};

function paginateRows(rows, params = {}) {
  const page = Math.max(1, Number(params.page || 1));
  const limit = Math.max(1, Number(params.limit || 20));
  const start = (page - 1) * limit;
  return { data: rows.slice(start, start + limit), pagination: { page, limit, total: rows.length, pages: Math.ceil(rows.length / limit) || 1 } };
}

// Best complete list we know for an endpoint (the full sync stores one under
// "<url>?__all"; otherwise the biggest page-list we happen to have seen).
async function bestList(url) {
  const entries = (await apiCacheEntries(`${url}?`)).filter(e => Array.isArray(e.value?.data));
  if (!entries.length) return null;
  const full = entries.find(e => e.key === `${url}?${ALL}`);
  if (full) return full.value.data;
  return entries.sort((a, b) => b.value.data.length - a.value.data.length)[0].value.data;
}

// ── Reads ───────────────────────────────────────────────────────────────────
// `url` is the endpoint path (e.g. '/patients'), `params` the query params.
export async function cachedGet(url, params, config = {}) {
  try {
    const res = await api.get(url, { params, ...config });
    // Keep the last good answer. Failing to cache must never fail the read.
    try { await apiCachePut(cacheKey(url, params), res.data); } catch { /* noop */ }
    return res;
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    const exact = await apiCacheGet(cacheKey(url, params));
    if (exact !== undefined) return { data: { ...exact, offline: true } };
    // List request with params we never made online (another page/search):
    // answer from the best full list we have.
    const list = await bestList(url);
    if (list) {
      const search = String(params?.search || '').trim().toLowerCase();
      const rows = search ? list.filter(r => walkText(r).toLowerCase().includes(search)) : list;
      const p = paginateRows(rows, params);
      return offlineResponse(p.data, { pagination: p.pagination, offline: true });
    }
    throw e;
  }
}

// GET /endpoint/:id — falls back to the row inside any cached list.
export async function cachedGetById(endpoint, id) {
  const url = `/${endpoint}/${id}`;
  try {
    const res = await api.get(url);
    try { await apiCachePut(cacheKey(url), res.data); } catch { /* noop */ }
    return res;
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    const exact = await apiCacheGet(cacheKey(url));
    if (exact !== undefined) return { data: { ...exact, offline: true } };
    const entries = await apiCacheEntries(`/${endpoint}?`);
    for (const entry of entries) {
      const row = (entry.value?.data || []).find?.(r => String(r._id) === String(id));
      if (row) return offlineResponse(row, { offline: true });
    }
    throw e;
  }
}

// ── Local application of a write to every remembered list of an endpoint ───
async function patchLists(endpoint, fn) {
  const entries = await apiCacheEntries(`/${endpoint}?`);
  for (const entry of entries) {
    if (!Array.isArray(entry.value?.data)) continue;
    const before = entry.value.data;
    const after = fn(before);
    const total = (entry.value.pagination?.total ?? before.length) + (after.length - before.length);
    await apiCachePut(entry.key, { ...entry.value, data: after, pagination: { ...(entry.value.pagination || {}), total } });
  }
}

// Used by sync.js once a queued create reached the server: the temporary row
// must disappear from the remembered lists (the real one arrives on the next
// online read).
export async function removeOfflineRowFromCaches(offlineId) {
  const entries = await apiCacheEntries('/');
  for (const entry of entries) {
    const rows = entry.value?.data;
    if (!Array.isArray(rows) || !rows.some(r => r && r._id === offlineId)) continue;
    await apiCachePut(entry.key, {
      ...entry.value,
      data: rows.filter(r => r._id !== offlineId),
      pagination: { ...(entry.value.pagination || {}), total: Math.max(0, (entry.value.pagination?.total ?? rows.length) - 1) },
    });
  }
}

// ── Writes ──────────────────────────────────────────────────────────────────
export async function offlineCreate(endpoint, data) {
  try { return await api.post(`/${endpoint}`, data); }
  catch (e) {
    if (!isNetworkError(e)) throw e;
    const id = genLocalId(endpoint);
    const row = { ...data, _id: id, createdAt: new Date().toISOString(), offline: true, syncStatus: 'pending' };
    await patchLists(endpoint, rows => [row, ...rows]);
    await enqueue({ method: 'POST', url: `/${endpoint}`, data: { ...data, clientOfflineId: id } });
    return offlineResponse(row, { offline: true });
  }
}

export async function offlineUpdate(endpoint, id, data) {
  const local = async () => {
    let merged = { ...data, _id: id };
    await patchLists(endpoint, rows => rows.map(r => {
      if (String(r._id) !== String(id)) return r;
      merged = { ...r, ...data, _id: r._id, syncStatus: 'pending' };
      return merged;
    }));
    if (isOfflineId(id)) {
      // Never reached the server: fold the edit into its queued create.
      const queue = await getQueue();
      const create = queue.find(q => q.method === 'POST' && q.data?.clientOfflineId === id);
      if (create) {
        await updateQueueItem(create.id, { ...create, data: { ...create.data, ...data, clientOfflineId: id } });
        return offlineResponse(merged, { offline: true });
      }
    }
    await enqueue({ method: 'PUT', url: `/${endpoint}/${id}`, data });
    return offlineResponse(merged, { offline: true });
  };
  if (isOfflineId(id)) return local();
  try { return await api.put(`/${endpoint}/${id}`, data); }
  catch (e) { if (!isNetworkError(e)) throw e; return local(); }
}

export async function offlineDelete(endpoint, id) {
  const local = async () => {
    await patchLists(endpoint, rows => rows.filter(r => String(r._id) !== String(id)));
    if (isOfflineId(id)) await purgeQueueReferencing(id);
    else await enqueue({ method: 'DELETE', url: `/${endpoint}/${id}` });
    return offlineResponse({ _id: id, deleted: true }, { offline: true });
  };
  if (isOfflineId(id)) return local();
  try { return await api.delete(`/${endpoint}/${id}`); }
  catch (e) { if (!isNetworkError(e)) throw e; return local(); }
}

// A write that is not a plain create/update/delete of a list row (approve,
// reject, convert, pay salary, add transaction, ...): try the network, else
// queue it and answer "accepted". `localRow` (optional) is shown meanwhile.
export async function offlineAction(method, url, data, { listEndpoint, localRow, patch } = {}) {
  const send = () => (method === 'POST' ? api.post(url, data) : method === 'PUT' ? api.put(url, data) : api.delete(url));
  try { return await send(); }
  catch (e) {
    if (!isNetworkError(e)) throw e;
    if (listEndpoint && patch) await patchLists(listEndpoint, patch);
    const id = localRow ? genLocalId('act') : undefined;
    await enqueue({ method, url, data: id ? { ...data, clientOfflineId: id } : data });
    return offlineResponse(localRow ? { ...localRow, _id: id, offline: true } : { queued: true }, { offline: true, message: 'Saved offline — will sync when online' });
  }
}

// Builds a createService()-compatible object for an endpoint.
export const createOfflineService = (endpoint) => ({
  getAll: (params) => cachedGet(`/${endpoint}`, params),
  getById: (id) => cachedGetById(endpoint, id),
  create: (data) => offlineCreate(endpoint, data),
  update: (id, data) => offlineUpdate(endpoint, id, data),
  delete: (id) => offlineDelete(endpoint, id),
});

// Called by the full sync (online, queue drained): remembers the COMPLETE list
// of every generic endpoint so those pages open offline even if the person
// never visited them online on this device.
export const GENERIC_ENDPOINTS = [
  'patients', 'prescriptions', 'employees', 'users', 'roles', 'branches', 'brands',
  'manufacturers', 'strengths', 'taxes', 'currencies', 'bank-accounts', 'requisitions',
  'purchase-requests', 'purchase-returns',
];

export async function warmGenericCaches(fetchAllWithTotal) {
  if (await hasPendingQueue()) return;
  await Promise.all(GENERIC_ENDPOINTS.map(async (endpoint) => {
    try {
      const { rows, total } = await fetchAllWithTotal(`/${endpoint}`);
      if (rows.length < total) return; // incomplete list — don't pretend it is complete
      await apiCachePut(`/${endpoint}?${ALL}`, { success: true, data: rows, pagination: { total: rows.length } });
    } catch { /* endpoint not permitted / not available — skip */ }
  }));
}

// ── Offline login ───────────────────────────────────────────────────────────
// After a successful online login the account (email + salted SHA-256 of the
// password — never the password itself) and its session are kept on this
// device, so the same person can log in again with no connection.
const CRED_KEY = '__offlineCredentials';

async function digest(email, password) {
  if (!globalThis.crypto?.subtle) return null;
  const bytes = new TextEncoder().encode(`pharmacare:${String(email).trim().toLowerCase()}:${password}`);
  const buf = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function login(credentials) {
  try {
    const res = await api.post('/auth/login', credentials);
    try {
      const hash = await digest(credentials.email, credentials.password);
      const session = res.data?.data;
      if (hash && session) {
        const all = (await apiCacheGet(CRED_KEY)) || {};
        all[String(credentials.email).trim().toLowerCase()] = { hash, session };
        await apiCachePut(CRED_KEY, all);
        if (session.user) await apiCachePut(cacheKey('/auth/me'), { success: true, data: session.user });
      }
    } catch { /* never block a good login on local bookkeeping */ }
    return res;
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    const fail = (message) => { const err = new Error(message); err.response = { status: 401, data: { message } }; return err; };
    const all = (await apiCacheGet(CRED_KEY)) || {};
    const entry = all[String(credentials.email || '').trim().toLowerCase()];
    if (!entry) throw fail('You are offline. Log in once with internet on this device before using offline login.');
    const hash = await digest(credentials.email, credentials.password);
    if (!hash || hash !== entry.hash) throw fail('Invalid email or password');
    return offlineResponse(entry.session, { offline: true });
  }
}
